<template>
  <div class="costume-shop">
    <div v-if="!game" class="loading">请选择游戏</div>
    <template v-else>
    <div class="header">
      <h2>角色列表</h2>
    </div>

    <!-- 角色选择标签 -->
    <div class="role-tabs">
      <el-button
        v-for="role in roles"
        :key="role.id"
        :type="selectedRoleId === role.id ? 'primary' : 'default'"
        @click="selectRole(role.id)"
        size="small"
      >
        {{ role.name }}
      </el-button>
      <el-select
        v-if="roles.length > 5"
        v-model="selectedRoleId"
        placeholder="角色（下拉单）"
        size="small"
        style="width: 120px; margin-left: 8px;"
        @change="selectRole"
      >
        <el-option
          v-for="role in roles"
          :key="role.id"
          :label="role.name"
          :value="role.id"
        />
      </el-select>
    </div>

    <!-- 当前角色的服装列表 -->
    <div v-if="selectedRole" class="role-section">
      <div class="section-header">
        <h3>{{ selectedRole.name }}</h3>
      </div>

      <div class="costume-grid">
        <!-- 已有服装卡片 -->
        <div class="costume-card" v-for="costume in costumes" :key="costume.id">
          <div class="costume-image">
            <img v-if="costume.image_url" :src="costume.image_url" alt="服装图片" />
            <div v-else class="image-placeholder">图片</div>
          </div>
          <!-- 只有本次选过的素材才显示文件名：表里存的是 url、没有 asset_id，
               重开页面无从反查素材名（url 里是 OSS 随机文件名，解析出来没意义） -->
          <div
            v-if="pickedNames[costume.image_url]"
            class="costume-asset-name"
            :title="pickedNames[costume.image_url]"
          >
            {{ pickedNames[costume.image_url] }}
          </div>
          <div class="costume-pick-bar" @click="openPicker(costume)">
            <i class="el-icon-picture-outline"></i>
            <span>{{ costume.image_url ? '更换图片' : '选择图片' }}</span>
          </div>
          <div class="costume-info">
            <el-input
              v-model="costume.name"
              placeholder="服装名称"
              size="small"
              class="costume-name-input"
            />
            <el-input
              v-model.number="costume.price"
              placeholder="价格"
              size="small"
              type="number"
              class="costume-price-input"
            />
          </div>
          <div class="costume-actions">
            <el-button size="mini" @click="saveCostume(costume)">保存</el-button>
            <el-button size="mini" type="danger" @click="removeCostume(costume)">删除</el-button>
          </div>
        </div>

        <!-- 新增服装卡片 -->
        <div class="costume-card new-costume-card" v-if="newCostume">
          <div
            class="costume-image costume-image-pickable"
            :title="newCostume.image_url ? '更换图片' : '选择图片'"
            @click="openPicker(newCostume)"
          >
            <img v-if="newCostume.image_url" :src="newCostume.image_url" alt="服装图片" />
            <i v-else class="el-icon-plus"></i>
          </div>
          <div
            v-if="pickedNames[newCostume.image_url]"
            class="costume-asset-name"
            :title="pickedNames[newCostume.image_url]"
          >
            {{ pickedNames[newCostume.image_url] }}
          </div>
          <!-- 与已有卡片同一条入口：选完图之后图区虽然还能点，但没有任何文字提示，
               作者看不出还能换——两类卡片结构保持一致 -->
          <div class="costume-pick-bar" @click="openPicker(newCostume)">
            <i class="el-icon-picture-outline"></i>
            <span>{{ newCostume.image_url ? '更换图片' : '选择图片' }}</span>
          </div>
          <div class="costume-info">
            <el-input
              v-model="newCostume.name"
              placeholder="服装名称"
              size="small"
              class="costume-name-input placeholder-text"
            />
            <el-input
              v-model.number="newCostume.price"
              placeholder="价格"
              size="small"
              type="number"
              class="costume-price-input placeholder-text"
            />
          </div>
          <div class="costume-actions">
            <el-button size="mini" @click="saveNewCostume">保存</el-button>
            <el-button size="mini" @click="cancelNewCostume">删除</el-button>
          </div>
        </div>

        <!-- 添加按钮卡片 -->
        <div class="costume-card add-card" @click="addNewCostume">
          <div class="add-icon">
            <i class="el-icon-plus"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 常驻空态：没有选中角色时给出原因与去处。定位失败时的 toast 3 秒就消失，
         不能是唯一的解释，否则作者只看到一排按钮、对着空壳猜 -->
    <div v-else class="costume-empty">
      <i class="el-icon-warning-outline costume-empty-icon"></i>
      <p class="costume-empty-text">{{ emptyHint }}</p>
    </div>

    <!-- 服装图在 C 端就是角色立绘（portrait_resolver.dart 把 costume 当作立绘的一个来源），
         所以只开「立绘」页签。game-id 有值才会出现「本书素材」页签 -->
    <AssetPickerDialog
      :visible.sync="pickerVisible"
      :kinds="['portrait']"
      :multiple="false"
      :game-id="game ? game.id : 0"
      @picked="onAssetPicked"
    />

    </template>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Role, getRoles } from '@/api/role'
import { Costume, getCostumeList, createCostume, updateCostume, deleteCostume } from '@/api/costume'
import { Game } from '@/api/game'
import type { Asset } from '@/api/asset'
import AssetPickerDialog from '@/components/asset/AssetPickerDialog.vue'

@Component({ components: { AssetPickerDialog } })
export default class CostumeShop extends Vue {
  @Prop({ required: true }) readonly game!: Game

  /** 从角色管理里打开时定位到该角色；工具栏入口不传，行为不变（B4） */
  @Prop({ default: null }) readonly initialRoleId!: number | null

  roles: Role[] = []
  selectedRoleId: number | null = null
  /** 没有选中角色时的常驻说明，由 loadRoles 按具体原因赋值 */
  emptyHint = '这本书还没有角色开启「展示在衣橱」。请到「角色管理」里选中角色，打开「展示在衣橱」开关。'
  costumes: Costume[] = []
  newCostume: Costume | null = null
  pickerVisible = false
  /** 选图目标：直接指向 costumes 里的某一项或 newCostume，选中后写它的 image_url */
  pickerTarget: Costume | null = null
  /** url -> 素材名，只记本次会话里选过的，供卡片显示文件名（见模板里的说明） */
  pickedNames: Record<string, string> = {}

  get selectedRole(): Role | undefined {
    return this.roles.find(r => r.id === this.selectedRoleId)
  }

  @Watch('game', { immediate: true })
  onGameChange(newGame: Game) {
    if (newGame && newGame.id) {
      this.loadRoles()
    }
  }

  async loadRoles() {
    try {
      const data = await getRoles(this.game.id)
      this.roles = data.filter((r: any) => r.show_cos === 1)
      // 指定了初始角色（从角色管理弹窗的「设置衣橱」进来）却不在衣橱列表里时，
      // 绝不能静默退回第一个角色——作者点的是 A 的衣橱，选中 B 会给错角色加服装。
      // 宁可一个都不选，并说清楚原因
      if (this.initialRoleId !== null) {
        const target = data.find(r => r.id === this.initialRoleId)
        if (this.roles.some(r => r.id === this.initialRoleId)) {
          this.selectRole(this.initialRoleId)
        } else if (target) {
          this.selectedRoleId = null
          const who = target.name ? `「${target.name}」` : '该角色'
          this.emptyHint = `${who}未开启「展示在衣橱」，衣橱里没有它。请回到角色编辑区打开「展示在衣橱」开关，再进来添加服装。`
          this.$message.warning('该角色未开启「展示在衣橱」，衣橱里没有它。请先在角色编辑区打开这个开关')
        } else {
          this.selectedRoleId = null
          this.emptyHint = '该角色已不存在（可能已被删除），无法打开它的衣橱。'
          this.$message.warning('该角色已不存在，无法打开它的衣橱')
        }
        return
      }
      if (this.roles.length > 0 && !this.selectedRoleId) {
        this.selectedRoleId = this.roles[0].id!
        this.loadCostumes()
      }
    } catch (error) {
      this.$message.error('加载角色失败')
    }
  }

  selectRole(roleId: number) {
    this.selectedRoleId = roleId
    this.newCostume = null
    this.loadCostumes()
  }

  async loadCostumes() {
    if (!this.selectedRoleId) return
    try {
      this.costumes = await getCostumeList(this.game.id, this.selectedRoleId)
    } catch (error) {
      this.$message.error('加载服装失败')
      this.costumes = []
    }
  }

  addNewCostume() {
    if (!this.selectedRoleId) return
    this.newCostume = {
      game_id: this.game.id,
      role_id: this.selectedRoleId,
      name: '',
      image_url: '',
      price: 0,
      is_default: 0,
      status: 1
    }
  }

  cancelNewCostume() {
    this.newCostume = null
  }

  async saveNewCostume() {
    if (!this.newCostume) return
    if (!this.newCostume.name) {
      this.$message.warning('请输入服装名称')
      return
    }

    try {
      await createCostume(this.newCostume)
      this.$message.success('创建成功')
      this.newCostume = null
      this.loadCostumes()
    } catch (error) {
      this.$message.error('创建失败')
    }
  }

  async saveCostume(costume: Costume) {
    if (!costume.id) return
    if (!costume.name) {
      this.$message.warning('请输入服装名称')
      return
    }

    try {
      await updateCostume(costume.id, {
        name: costume.name,
        description: costume.description,
        image_url: costume.image_url,
        price: costume.price,
        is_default: costume.is_default,
        status: costume.status
      })
      this.$message.success('保存成功')
    } catch (error) {
      this.$message.error('保存失败')
    }
  }

  async removeCostume(costume: Costume) {
    if (!costume.id) return

    try {
      await this.$confirm('确定删除该服装？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })

      await deleteCostume(costume.id)
      this.$message.success('删除成功')
      this.loadCostumes()
    } catch (error) {
      if (error !== 'cancel') {
        this.$message.error('删除失败')
      }
    }
  }

  /** 新增卡片与已有卡片共用一个选择器：目标就是那个 Costume 对象本身，
      两者的 image_url 都在对象创建时已初始化，赋值是响应式的 */
  openPicker(costume: Costume) {
    this.pickerTarget = costume
    this.pickerVisible = true
  }

  /** 选中即写本地，与改名称/价格一致，仍需点「保存」才持久化。
      选择器交回的 Asset 就是权威数据（可能是作者刚上传的新素材），直接用 */
  onAssetPicked(a: Asset) {
    if (!this.pickerTarget) return
    this.pickerTarget.image_url = a.url
    this.$set(this.pickedNames, a.url, a.name)
    this.pickerTarget = null
  }
}
</script>

<style scoped>
.costume-shop {
  padding: 20px;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #909399;
  font-size: 14px;
}

.header {
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  padding: 10px 20px;
  border: 1px solid #dcdfe6;
  display: inline-block;
}

.role-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  padding: 10px;
  border: 1px solid #dcdfe6;
}

.costume-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 220px;
  padding: 32px 24px;
  color: #909399;
  text-align: center;
}

.costume-empty-icon {
  font-size: 34px;
  color: #e6a23c;
}

.costume-empty-text {
  max-width: 520px;
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
}

.role-section {
  border: 1px solid #dcdfe6;
  padding: 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  padding: 10px 20px;
  border: 1px solid #dcdfe6;
  display: inline-block;
}

.costume-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.costume-card {
  border: 1px solid #dcdfe6;
  width: 180px;
  display: flex;
  flex-direction: column;
}

.costume-image {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #dcdfe6;
  overflow: hidden;
}

.costume-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-placeholder {
  color: #909399;
  font-size: 14px;
}

.costume-image-pickable {
  cursor: pointer;
}

.costume-image-pickable:hover {
  background-color: #f5f7fa;
}

.costume-image-pickable .el-icon-plus {
  font-size: 40px;
  color: #909399;
}

.costume-asset-name {
  padding: 4px 10px;
  border-bottom: 1px solid #dcdfe6;
  color: #606266;
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.costume-info {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.costume-name-input,
.costume-price-input {
  width: 100%;
}

.placeholder-text ::v-deep input::placeholder {
  color: #c0c4cc;
}

.costume-actions {
  padding: 10px;
  display: flex;
  gap: 8px;
  border-top: 1px solid #dcdfe6;
}

.costume-actions .el-button {
  flex: 1;
}

.add-card {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
}

.add-card:hover {
  background-color: #f5f7fa;
}

.add-icon {
  font-size: 60px;
  color: #909399;
}

.costume-pick-bar {
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-bottom: 1px solid #dcdfe6;
  color: #909399;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.costume-pick-bar:hover {
  background-color: #ecf5ff;
  color: #409eff;
}
</style>
